import logoTuSzama from '@/assets/druk/tuszama-logo.png';

/**
 * WYDRUK LISTY POSIŁKÓW DLA KUCHNI (zlecenie OWNERA 19.10) — „Dzisiejsze zamówienia” → ramki
 * „Posiłki — 1. wyjazd” (zamówienia do 10:00) i „Posiłki — 2. wyjazd” (po 10:00).
 *
 * Ta sama karta A4 co w starym panelu restauracji (tuszama-panel `_helpers/wydruk-posilkow.ts`): oryginalne logo
 * TuSzama, restauracja, data, nazwa sekcji, tabela Lp. / Posiłek / Sztuk w kolejności ramki, suma. Wydruk to
 * MIGAWKA chwili kliknięcia z tych samych pozycji, które rysuje wykres — bez osobnego liczenia.
 * Druk w ukrytym iframe (bez okna wyskakującego i bez nawigacji).
 */

export interface PozycjaWydruku { nazwa: string; ilosc: number }

export interface DaneWydruku {
  restauracja: string;
  /** Dzień dostawy „DD.MM.RRRR”. */
  data: string;
  /** Chwila wydruku „DD.MM.RRRR, GG:MM” (Warszawa). */
  wydrukowano: string;
  sekcja: string;
  pozycje: PozycjaWydruku[];
  /** Pełny adres logo (domyślnie zasób CC). */
  logoUrl?: string;
  /** Krój (wartość tokenu `--krój-ui`); domyślnie odczyt z konsoli. */
  kroj?: string;
}

const ESCAPE: Readonly<Record<string, string>> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

export function ucieknij(tekst: unknown): string {
  return String(tekst ?? '').replace(/[&<>"']/g, (z) => ESCAPE[z] ?? z);
}

/**
 * Gęstość tak, żeby lista zmieściła się na JEDNEJ stronie A4 (pion, marginesy 12 mm). Korekta kuchni 19.10:
 * większy tekst i wyższe wiersze (miejsce na długopis), kolumna „Uwagi” na prawej 1/3 strony; dwie kolumny
 * (bez „Uwag”) dopiero powyżej 50 pozycji. Wysokość: (pozycje + nagłówek) × wiersz ≤ ok. 220 mm.
 */
export function gestosc(liczba: number): { kolumny: 1 | 2; czcionkaPt: number; wierszMm: number } {
  if (liczba <= 20) return { kolumny: 1, czcionkaPt: 14, wierszMm: 9.5 };
  if (liczba <= 26) return { kolumny: 1, czcionkaPt: 13, wierszMm: 8 };
  if (liczba <= 32) return { kolumny: 1, czcionkaPt: 12, wierszMm: 6.6 };
  if (liczba <= 40) return { kolumny: 1, czcionkaPt: 11, wierszMm: 5.3 };
  if (liczba <= 50) return { kolumny: 1, czcionkaPt: 10, wierszMm: 4.2 };
  if (liczba <= 90) return { kolumny: 2, czcionkaPt: 9, wierszMm: 4.6 };
  return { kolumny: 2, czcionkaPt: 7, wierszMm: 3.4 };
}

function tabela(pozycje: readonly PozycjaWydruku[], od: number, uwagi: boolean): string {
  // Kolumna „Uwagi” = pusta prawa część wiersza na ręczne dopiski; linia wiersza przechodzi przez całą szerokość.
  const pusta = uwagi ? '<td class="uwagi"></td>' : '';
  const wiersze = pozycje.map((p, i) => `<tr><td class="lp">${od + i + 1}.</td><td class="nazwa">${ucieknij(p.nazwa)}</td>`
    + `<td class="ilosc">${p.ilosc}</td>${pusta}</tr>`).join('');
  const kolumny = '<colgroup><col class="lp"><col class="nazwa"><col class="ilosc">'
    + (uwagi ? '<col class="uwagi">' : '') + '</colgroup>';
  return `<table>${kolumny}<thead><tr><th class="lp">Lp.</th><th class="nazwa">Posiłek</th><th class="ilosc">Sztuk</th>`
    + (uwagi ? '<th class="uwagi">Uwagi</th>' : '') + `</tr></thead><tbody>${wiersze}</tbody></table>`;
}

/**
 * Krój wydruku = TOKEN Control Center (`--krój-ui`, reguła typografii T1): odczytany z konsoli w chwili druku
 * i podany dokumentowi wydruku jako ten sam token. Bez odczytu — systemowy bezszeryfowy.
 */
export function krojZTokenu(): string {
  const wartosc = typeof document === 'undefined' ? ''
    : getComputedStyle(document.documentElement).getPropertyValue('--krój-ui').trim();
  return /^[\w\s'",.-]{1,120}$/.test(wartosc) && !/var\(/.test(wartosc) ? wartosc : 'sans-serif';
}

export function logoWydruku(): string {
  return new URL(logoTuSzama, document.baseURI).href;
}

/** Pełny dokument HTML do druku (A4 pion, jedna strona, bez elementów konsoli). */
export function dokumentWydruku(d: DaneWydruku): string {
  const g = gestosc(d.pozycje.length);
  const suma = d.pozycje.reduce((a, p) => a + p.ilosc, 0);
  const polowa = Math.ceil(d.pozycje.length / 2);
  const tresc = d.pozycje.length === 0
    ? '<p class="pusto">Brak pozycji w tej sekcji.</p>'
    : g.kolumny === 1
      ? tabela(d.pozycje, 0, true)
      : `<div class="kolumny"><div>${tabela(d.pozycje.slice(0, polowa), 0, false)}</div><div>${tabela(d.pozycje.slice(polowa), polowa, false)}</div></div>`;
  return `<!doctype html><html lang="pl"><head><meta charset="utf-8"><title>${ucieknij(d.sekcja)} — ${ucieknij(d.restauracja)}</title>
<style>
  @page { size: A4 portrait; margin: 12mm; }
  * { box-sizing: border-box; }
  :root { --krój-ui: ${d.kroj ?? krojZTokenu()}; }
  html, body { margin: 0; padding: 0; background: #fff; color: #111; }
  body { font-family: var(--krój-ui); font-size: calc(${g.czcionkaPt}pt * var(--skala, 1)); }
  .naglowek { display: flex; align-items: center; gap: 8mm; border-bottom: 0.6mm solid #111; padding-bottom: 3mm; margin-bottom: 4mm; }
  .naglowek img { height: 16mm; width: auto; }
  .restauracja { font-size: 16pt; font-weight: 700; line-height: 1.15; }
  .meta { font-size: 10pt; color: #333; margin-top: 1mm; }
  h1 { font-size: 15pt; margin: 0 0 1.5mm 0; text-align: left; }
  .suma { font-size: 11pt; margin: 0 0 9mm 0; }
  table { width: 100%; border-collapse: collapse; table-layout: fixed; }
  col.lp { width: 12mm; } col.ilosc { width: 22mm; } col.uwagi { width: 34%; }
  th, td { text-align: left; border-bottom: 0.35mm solid #333; padding: 0 2.5mm; height: calc(${g.wierszMm}mm * var(--skala, 1)); vertical-align: middle; }
  th { font-size: 0.9em; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; border-bottom: 0.6mm solid #111; }
  td.lp, th.lp { color: #333; }
  td.ilosc, th.ilosc { text-align: right; font-weight: 700; }
  td.nazwa { overflow-wrap: anywhere; }
  td.uwagi, th.uwagi { border-left: 0.25mm solid #999; }
  th.uwagi { color: #555; font-weight: 600; }
  .kolumny { display: flex; gap: 6mm; }
  .kolumny > div { flex: 1; min-width: 0; }
  .pusto { font-size: 12pt; }
  * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
</style></head><body>
<div class="naglowek"><img src="${ucieknij(d.logoUrl ?? logoWydruku())}" alt="TuSzama">
<div><div class="restauracja">${ucieknij(d.restauracja)}</div>
<div class="meta">Data: <b>${ucieknij(d.data)}</b> · wydrukowano ${ucieknij(d.wydrukowano)}</div></div></div>
<h1>${ucieknij(d.sekcja)}</h1>
<p class="suma">Razem: <b>${suma}</b> szt. · ${d.pozycje.length} ${d.pozycje.length === 1 ? 'pozycja' : 'pozycji'}</p>
${tresc}
</body></html>`;
}

/** „DD.MM.RRRR” z 'RRRR-MM-DD'. */
export function dataPl(iso: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? '');
  return m ? `${m[3]}.${m[2]}.${m[1]}` : '';
}

/** Chwila wydruku w czasie warszawskim: „DD.MM.RRRR, GG:MM”. */
export function chwilaWydruku(teraz: Date = new Date()): string {
  return teraz.toLocaleString('pl-PL', {
    timeZone: 'Europe/Warsaw', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

/**
 * Ostatnia linia obrony „1 strona A4”: gdy długie nazwy zawiną się do dwóch linii i lista przestanie mieścić się
 * na stronie, jednolicie zmniejsza tekst i wysokość wierszy tabeli (zmienna --skala), aż się zmieści — bez ucinania
 * i bez zmiany treści. Mierzy przy szerokości obszaru druku (A4 − marginesy = 186 × 273 mm, zapas 3 mm).
 */
export function dopasujDoStrony(doc: Document): number {
  const mm = 96 / 25.4;
  let skala = 1;
  while (skala > 0.6 && doc.body.getBoundingClientRect().height > 270 * mm) {
    skala = Math.round((skala - 0.05) * 100) / 100;
    doc.documentElement.style.setProperty('--skala', String(skala));
  }
  return skala;
}

/** Drukuje dokument w ukrytym iframe; czeka na logo (maks. 3 s); iframe sprząta się po minucie. */
export function drukujDokument(html: string, dokument: Document = document): HTMLIFrameElement {
  const ramka = dokument.createElement('iframe');
  ramka.setAttribute('aria-hidden', 'true');
  ramka.setAttribute('data-wydruk', 'posilki');
  ramka.style.cssText = 'position:fixed;right:0;bottom:0;width:186mm;height:0;border:0;visibility:hidden;';
  dokument.body.appendChild(ramka);
  const okno = ramka.contentWindow;
  if (!okno) return ramka;
  okno.document.open();
  okno.document.write(html);
  okno.document.close();
  let wydrukowano = false;
  const drukuj = () => {
    if (wydrukowano) return;
    wydrukowano = true;
    dopasujDoStrony(okno.document);
    okno.focus();
    okno.print();
    window.setTimeout(() => ramka.remove(), 60_000);
  };
  const logo = okno.document.querySelector('img');
  if (logo && !logo.complete) {
    logo.addEventListener('load', drukuj);
    logo.addEventListener('error', drukuj);
    window.setTimeout(drukuj, 3000);
  } else {
    window.setTimeout(drukuj, 0);
  }
  return ramka;
}
